"use client";

import {useEffect, useRef, useState} from "react";
import {hasProposalChanges} from "@/services/wingman/mutation-proposal";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import {resolveToolApprovalStatus} from "./toolApprovalStatus";

/** One decision per approval: duplicate clicks, expired proposals and settled calls cannot submit. */
export function useToolApprovalDecision({call, completed, result, active, disabled, onRespond}: {
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<void>;
}) {
   const approval = call.approval;
   const submitting = useRef(false);
   const [pending, setPending] = useState<"apply" | "discard" | null>(null);
   const [error, setError] = useState(false);
   const [now, setNow] = useState(Date.now);
   const settled = completed || approval?.approved !== undefined;
   const expiresAt = approval?.proposal ? Date.parse(approval.proposal.expiresAt) : undefined;
   useEffect(() => {
      if (expiresAt === undefined || settled) return;
      const timer = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(timer);
   }, [expiresAt, settled]);

   const expired = expiresAt !== undefined && expiresAt <= now;
   const canRespond = !!approval && active && !settled && !disabled && !pending && !!onRespond;
   // A preview that could not be read must never fall back to approving unreviewed changes.
   const canApply = canRespond && !expired && !approval?.previewUnavailable &&
      (!approval?.proposal || hasProposalChanges(approval.proposal));
   const status = resolveToolApprovalStatus(!!pending, approval?.approved, completed, active, result);

   const respond = async (approved: boolean) => {
      if (!approval || !canRespond || submitting.current || !onRespond || (approved && !canApply)) return;
      submitting.current = true;
      setPending(approved ? "apply" : "discard");
      setError(false);
      try {
         await onRespond({id: approval.id, approved});
      } catch {
         setError(true);
      } finally {
         submitting.current = false;
         setPending(null);
      }
   };

   return {
      status, settled, expired, error, pending, canRespond, canApply, respond,
      remainingMs: expiresAt === undefined ? undefined : Math.max(0, expiresAt - now),
   };
}
